/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */

export const COMPANY_ONBOARDING_HANDOFF_KEY = 'hrm_onboarding_company_handoff';
export const CANDIDATE_ONBOARDING_HANDOFF_KEY = 'hrm_onboarding_candidate_handoff';

export const COMPANY_ONBOARDING_PARAMETER_NAMES = ['azienda', 'email', 'p_iva', 'phone'] as const;
export const CANDIDATE_ONBOARDING_PARAMETER_NAMES = ['first_name', 'last_name', 'email', 'phone'] as const;

type HandoffStorage = Pick<Storage, 'getItem' | 'removeItem'>;
type SearchParams = Pick<URLSearchParams, 'get'>;
type HandoffHistoryState = {
  hrmOnboardingHandoff?: {
    storageKey: string;
    data: unknown;
  };
};

export type OnboardingHandoff = Record<string, string>;

const filterHandoff = (data: unknown, parameterNames: readonly string[]): OnboardingHandoff | null => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const handoff = parameterNames.reduce<OnboardingHandoff>((result, parameterName) => {
    const value = (data as Record<string, unknown>)[parameterName];
    if (typeof value === 'string') result[parameterName] = value;
    return result;
  }, {});

  return Object.keys(handoff).length > 0 ? handoff : null;
};

const readHistoryHandoff = (storageKey: string, parameterNames: readonly string[]): OnboardingHandoff | null => {
  if (typeof window === 'undefined') return null;

  const fallback = (window.history.state as HandoffHistoryState | null)?.hrmOnboardingHandoff;
  if (!fallback || fallback.storageKey !== storageKey) return null;

  return filterHandoff(fallback.data, parameterNames);
};

const getSessionStorage = (): HandoffStorage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const readStoredOnboardingHandoff = (
  storageKey: string,
  parameterNames: readonly string[],
  storage: HandoffStorage | null = getSessionStorage(),
): OnboardingHandoff | null => {
  if (!storage) return readHistoryHandoff(storageKey, parameterNames);

  try {
    const rawHandoff = storage.getItem(storageKey);
    if (!rawHandoff) return readHistoryHandoff(storageKey, parameterNames);

    const parsedHandoff: unknown = JSON.parse(rawHandoff);
    const handoff = filterHandoff(parsedHandoff, parameterNames);

    if (!handoff) {
      storage.removeItem(storageKey);
      return readHistoryHandoff(storageKey, parameterNames);
    }

    return handoff;
  } catch {
    try {
      storage.removeItem(storageKey);
    } catch {
      // Ignore unavailable browser storage and try the in-memory history fallback.
    }
    return readHistoryHandoff(storageKey, parameterNames);
  }
};

export const removeStoredOnboardingHandoff = (
  storageKey: string,
  storage: HandoffStorage | null = getSessionStorage(),
) => {
  if (storage) {
    try {
      storage.removeItem(storageKey);
    } catch {
      // The handoff is already held in component state when removal is attempted.
    }
  }

  if (typeof window !== 'undefined') {
    const historyState = window.history.state as HandoffHistoryState | null;
    if (historyState?.hrmOnboardingHandoff?.storageKey === storageKey) {
      const remainingState = { ...historyState };
      delete remainingState.hrmOnboardingHandoff;
      window.history.replaceState(remainingState, document.title);
    }
  }
};

export const getLegacyOnboardingValue = (
  handoff: OnboardingHandoff | null,
  searchParams: SearchParams,
  parameterName: string,
) => handoff?.[parameterName] || searchParams.get(parameterName);
